import { createContext, useContext, ReactNode, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, PabSession, SelectedOkr, SelectedAdvisor } from "@/services/api";
import { useToast } from "@/hooks/useToast";
import { useAuth } from "@/hooks/useAuth";
import { hasRole } from "@/utils/roleUtils";
import { PAB_SESSION_LOAD_ERROR, GOALS_LOAD_ERROR, PAB_ADVISORS_LOAD_ERROR } from "@/utils/toastMessages";

/**
 * Context interface for PAB session data
 * Provides access to the active PAB session, selected OKRs, and advisors
 */
interface PabSessionContextType {
  /** The active PAB session or null if no session exists */
  session: PabSession | null;
  /** Array of selected OKRs for the current session */
  goals: SelectedOkr[];
  /** Array of selected advisors for the current session */
  advisors: SelectedAdvisor[];
  /** Whether any of the queries are currently loading */
  isLoading: boolean;
  /** Whether any of the queries have encountered an error */
  isError: boolean;
  /** Error object if any query failed */
  error: Error | null;
  /** Function to refetch all session data */
  refetch: () => void;
}

const PabSessionContext = createContext<PabSessionContextType | undefined>(undefined);

/**
 * Hook to use PAB session context
 */
export const usePabSession = () => {
  const context = useContext(PabSessionContext);
  if (context === undefined) {
    throw new Error("usePabSession must be used within a PabSessionProvider");
  }
  return context;
};

/**
 * Provider component for PAB session data
 * Manages PAB session state using TanStack Query for caching and synchronization
 */
interface PabSessionProviderProps {
  children: ReactNode;
}

export const PabSessionProvider = ({ children }: PabSessionProviderProps) => {
  const { showToast } = useToast();
  const { user } = useAuth();
  // Only query active PAB session for achievers, not advisors
  const {
    data: session,
    isLoading: sessionLoading,
    isError: sessionError,
    error: sessionErrorData,
    refetch: refetchSession,
  } = useQuery({
    queryKey: ["pab-session", "active"],
    queryFn: api.pabSessions.getActiveSession,
    retry: 1,
    staleTime: 60 * 60 * 1000, // 1 hour
    enabled: hasRole(user, "achiever"), // Only run for achievers
  });
  // Query for selected OKRs (depends on session and user role)
  const {
    data: goals = [],
    isLoading: goalsLoading,
    isError: goalsError,
    error: goalsErrorData,
    refetch: refetchGoals,
  } = useQuery({
    queryKey: ["pab-session", "okrs", session?.id],
    queryFn: () => api.pabSessions.getSelectedOkrs(session!.id),
    enabled: !!session?.id && hasRole(user, "achiever"),
    retry: 1,
    staleTime: 60 * 60 * 1000, // 1 hour
  });

  // Query for selected advisors (depends on session and user role)
  const {
    data: advisors = [],
    isLoading: advisorsLoading,
    isError: advisorsError,
    error: advisorsErrorData,
    refetch: refetchAdvisors,
  } = useQuery({
    queryKey: ["pab-session", "advisors", session?.id],
    queryFn: () => api.pabSessions.getSelectedAdvisors(session!.id),
    enabled: !!session?.id && hasRole(user, "achiever"),
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Combined loading state
  const isLoading = sessionLoading || goalsLoading || advisorsLoading;

  // Combined error state
  const isError = sessionError || goalsError || advisorsError;
  const error = sessionErrorData || goalsErrorData || advisorsErrorData;

  // Show error notifications only for achievers
  useEffect(() => {
    if (hasRole(user, "achiever")) {
      if (sessionError && sessionErrorData) {
        showToast({ type: "error", message: PAB_SESSION_LOAD_ERROR });
      }
      if (goalsError && goalsErrorData) {
        showToast({ type: "error", message: GOALS_LOAD_ERROR });
      }
      if (advisorsError && advisorsErrorData) {
        showToast({ type: "error", message: PAB_ADVISORS_LOAD_ERROR });
      }
    }
  }, [
    user,
    sessionError,
    goalsError,
    advisorsError,
    sessionErrorData,
    goalsErrorData,
    advisorsErrorData,
    showToast,
  ]);

  // Refetch all data (only for achievers)
  const refetch = () => {
    if (hasRole(user, "achiever")) {
      refetchSession();
      if (session?.id) {
        refetchGoals();
        refetchAdvisors();
      }
    }
  };

  const value: PabSessionContextType = {
    session: session || null,
    goals,
    advisors,
    isLoading,
    isError,
    error,
    refetch,
  };

  return <PabSessionContext.Provider value={value}>{children}</PabSessionContext.Provider>;
};
